// APP#26 — browser geofence presence. Browsers do not provide a durable
// background geofence primitive, so this path is explicitly foreground-only.
// Coordinates/radius remain in localStorage; only enter/exit automation rules
// and their device actions go to REST.
import { ApiError, apiFetch } from '../../lib/api';
import { canControlDevice, type DeviceSummary } from '../devices/deviceModel';
import { CL_MOTION, CL_OBJECT, MOTION_ENABLED, OBJECT_ENABLED } from '../devices/settings/ids';

const STORAGE_KEY = 'ls.geofence.presence.v1';
export const DEFAULT_GEOFENCE_RADIUS_M = 150;

export type GeofenceTransition = 'enter' | 'exit';

export interface GeofencePresenceConfig {
  latitude: number;
  longitude: number;
  radiusM: number;
  enterAutomationId: string;
  exitAutomationId: string;
  lastInside: boolean;
  pendingTransition?: GeofenceTransition;
}

export interface GeofenceAutomationAction {
  type: 'op_set';
  device_id: string;
  op: 'set';
  id: string;
  value: boolean;
}

// 21 §2.1：owner/admin 或 shared-user 具 `control` permission（缺 role fail-closed）。
function canControl(d: DeviceSummary): boolean {
  return canControlDevice(d);
}

export function buildGeofenceActions(devices: DeviceSummary[], value: boolean): GeofenceAutomationAction[] {
  const actions: GeofenceAutomationAction[] = [];
  for (const d of devices) {
    if (!canControl(d)) continue;
    const clusters = new Set((d.capability_effective ?? []).map((c) => c.cluster?.toLowerCase()).filter(Boolean));
    if (clusters.has(CL_MOTION)) actions.push({ type: 'op_set', device_id: d.device_id, op: 'set', id: MOTION_ENABLED, value });
    if (clusters.has(CL_OBJECT)) actions.push({ type: 'op_set', device_id: d.device_id, op: 'set', id: OBJECT_ENABLED, value });
  }
  return actions;
}

export function distanceMeters(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLon = (bLon - aLon) * rad;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function transitionForSample(lastInside: boolean | null, inside: boolean): GeofenceTransition | null {
  if (lastInside === null || lastInside === inside) return null;
  return inside ? 'enter' : 'exit';
}

export function readGeofenceConfig(): GeofencePresenceConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<GeofencePresenceConfig>;
    if (![value.latitude, value.longitude, value.radiusM].every((v) => typeof v === 'number') ||
        !value.enterAutomationId || !value.exitAutomationId || typeof value.lastInside !== 'boolean') return null;
    return value as GeofencePresenceConfig;
  } catch { return null; }
}

function writeConfig(value: GeofencePresenceConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function clearGeofencePresence(): void {
  localStorage.removeItem(STORAGE_KEY);
}

async function createRule(name: string, transition: GeofenceTransition, actions: GeofenceAutomationAction[]): Promise<string> {
  const response = await apiFetch<{ automation_id: string }>('/v3/automations', {
    method: 'POST',
    idempotencyKey: crypto.randomUUID(),
    body: { name, enabled: true, trigger: { type: 'geofence', transition }, actions, mode: 'single', cooldown_s: 60 },
  });
  return response.data.automation_id;
}

export async function enableGeofencePresence(
  devices: DeviceSummary[], latitude: number, longitude: number, radiusM = DEFAULT_GEOFENCE_RADIUS_M,
): Promise<GeofencePresenceConfig> {
  const away = buildGeofenceActions(devices, true);
  const home = buildGeofenceActions(devices, false);
  if (away.length === 0 || home.length === 0) throw new Error('NO_DETECTION_DEVICES');
  const existing = readGeofenceConfig();
  if (existing) await disableGeofencePresence();

  let exitId: string | null = null;
  let enterId: string | null = null;
  try {
    exitId = await createRule('離家自動警戒', 'exit', away);
    enterId = await createRule('到家自動放鬆警戒', 'enter', home);
    const config: GeofencePresenceConfig = {
      latitude, longitude, radiusM: Math.max(100, Math.min(1000, radiusM)),
      enterAutomationId: enterId, exitAutomationId: exitId, lastInside: true,
    };
    writeConfig(config);
    return config;
  } catch (error) {
    if (enterId) await apiFetch(`/v3/automations/${encodeURIComponent(enterId)}`, { method: 'DELETE' }).catch(() => undefined);
    if (exitId) await apiFetch(`/v3/automations/${encodeURIComponent(exitId)}`, { method: 'DELETE' }).catch(() => undefined);
    throw error;
  }
}

export async function disableGeofencePresence(): Promise<void> {
  const config = readGeofenceConfig();
  clearGeofencePresence();
  if (!config) return;
  await Promise.allSettled([
    apiFetch(`/v3/automations/${encodeURIComponent(config.enterAutomationId)}`, { method: 'DELETE' }),
    apiFetch(`/v3/automations/${encodeURIComponent(config.exitAutomationId)}`, { method: 'DELETE' }),
  ]);
}

let firing = false;

async function flushPending(config: GeofencePresenceConfig): Promise<void> {
  if (!config.pendingTransition || firing) return;
  firing = true;
  const transition = config.pendingTransition;
  const id = transition === 'exit' ? config.exitAutomationId : config.enterAutomationId;
  try {
    await apiFetch(`/v3/automations/${encodeURIComponent(id)}/fire`, {
      method: 'POST', idempotencyKey: crypto.randomUUID(),
      body: { correlation_id: `geo-${transition}-${crypto.randomUUID()}` },
    });
    const current = readGeofenceConfig();
    if (current?.pendingTransition === transition) {
      delete current.pendingTransition;
      writeConfig(current);
    }
  } catch (error) {
    // Cooldown means the edge was already accepted recently; clear the retry.
    if (error instanceof ApiError && error.code === 'AUTOMATION_COOLDOWN') {
      const current = readGeofenceConfig();
      if (current?.pendingTransition === transition) {
        delete current.pendingTransition;
        writeConfig(current);
      }
    }
  } finally { firing = false; }
}

/** Starts foreground-only watchPosition and returns its disposer. */
export function watchGeofencePresence(onError?: (message: string) => void): () => void {
  if (!navigator.geolocation) { onError?.('GEOLOCATION_UNAVAILABLE'); return () => undefined; }
  const initial = readGeofenceConfig();
  if (!initial) return () => undefined;
  void flushPending(initial);
  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const config = readGeofenceConfig();
      if (!config) return;
      const inside = distanceMeters(config.latitude, config.longitude, position.coords.latitude, position.coords.longitude) <= config.radiusM;
      const transition = transitionForSample(config.lastInside, inside);
      if (transition) config.pendingTransition = transition;
      config.lastInside = inside;
      writeConfig(config);
      void flushPending(config);
    },
    (error) => onError?.(error.message),
    { enableHighAccuracy: false, maximumAge: 60_000, timeout: 20_000 },
  );
  return () => navigator.geolocation.clearWatch(watchId);
}
